/**
 * SaveBrandProfileModal — folder-selection popup for saving an AI Brand
 * Profile snapshot to the founder's Projects. Mirrors the visual + flow
 * of SaveFinancialModal so both save-to-project experiences feel identical.
 *
 * Design contract:
 *   - GET /projects → filter is_folder=true to populate the folder list
 *   - POST /projects → {type:"brand_profile", parent_id, name, brand_profile_snapshot}
 *   - Emits `onSaved(project)` on success so the caller can toast + close.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function SaveBrandProfileModal({ profile, defaultName, onCancel, onSaved }) {
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelected] = useState("");
  const [name, setName] = useState(
    defaultName || `AI Brand Profile — ${new Date().toLocaleDateString()}`,
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/projects")
      .then(({ data }) => {
        if (cancelled) return;
        const rows = Array.isArray(data) ? data : data.projects || [];
        setFolders(rows.filter((r) => r.is_folder));
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Give this snapshot a name.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/projects", {
        name: trimmed,
        type: "brand_profile",
        parent_id: selectedFolder || null,
        brand_profile_snapshot: profile,
      });
      toast.success("Brand profile saved to Projects.");
      onSaved && onSaved(data);
    } catch (e) {
      toast.error("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid="brand-profile-save-modal"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-background shadow-2xl border border-shell-border overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-shell-border">
          <h3 className="font-display text-lg">Save Brand Profile to Projects</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Snapshots your current AI Brand Profile so you can restore it later without losing the live copy.
          </p>
        </div>
        <div className="p-5 space-y-4">
          <label className="block text-sm">
            <span className="font-semibold">Snapshot name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full rounded-md border border-shell-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="brand-profile-save-name"
              autoFocus
            />
          </label>
          <label className="block text-sm">
            <span className="font-semibold">Folder</span>
            <select
              value={selectedFolder}
              onChange={(e) => setSelected(e.target.value)}
              className="mt-1 w-full rounded-md border border-shell-border bg-background px-3 py-2 text-sm"
              data-testid="brand-profile-save-folder"
              disabled={loading}
            >
              <option value="">(Root — no folder)</option>
              {folders.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="p-5 flex items-center justify-end gap-2 border-t border-shell-border bg-muted/30">
          <button
            type="button"
            onClick={onCancel}
            className="text-sm px-4 py-2 rounded-md text-muted-foreground hover:text-foreground"
            data-testid="brand-profile-save-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="btn-primary text-sm disabled:opacity-60"
            data-testid="brand-profile-save-confirm"
          >
            {saving ? "Saving…" : "Save to Projects"}
          </button>
        </div>
      </div>
    </div>
  );
}
